import React from 'react';
import { IconButton, Box, Switch, useTheme } from '@mui/material';
import { DarkMode, LightMode, WbSunny, NightlightRound } from '@mui/icons-material';
import { useAppTheme } from '@contexts/ThemeContext';

interface ThemeToggleProps {
  variant?: 'switch' | 'icon';
  size?: 'small' | 'medium' | 'large';
  position?: 'fixed' | 'relative';
}

export const ThemeToggle: React.FC<ThemeToggleProps> = ({
  variant = 'switch',
  size = 'medium',
  position = 'fixed'
}) => {
  const { isDarkMode, toggleTheme } = useAppTheme();
  const theme = useTheme();

  if (variant === 'icon') {
    return (
      <IconButton
        onClick={toggleTheme}
        size={size}
        sx={{
          ...(position === 'fixed' && {
            position: 'fixed',
            top: 16,
            right: 16,
            zIndex: 1300,
          }),
          color: isDarkMode ? '#667eea' : '#ffa500',
          backgroundColor: isDarkMode
            ? 'rgba(102, 126, 234, 0.1)'
            : 'rgba(255, 165, 0, 0.1)',
          border: `1px solid ${isDarkMode ? 'rgba(102, 126, 234, 0.3)' : 'rgba(255, 165, 0, 0.3)'}`,
          borderRadius: '12px',
          transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
          '&:hover': {
            backgroundColor: isDarkMode
              ? 'rgba(102, 126, 234, 0.2)'
              : 'rgba(255, 165, 0, 0.2)',
            transform: 'scale(1.05)',
            boxShadow: isDarkMode
              ? '0 4px 12px rgba(102, 126, 234, 0.3)'
              : '0 4px 12px rgba(255, 165, 0, 0.3)',
          },
          '&:active': {
            transform: 'scale(0.95)',
          }
        }}
      >
        {isDarkMode ? <NightlightRound /> : <WbSunny />}
      </IconButton>
    );
  }

  return (
    <Box
      sx={{
        ...(position === 'fixed' && {
          position: 'fixed',
          top: 16,
          right: 16,
          zIndex: 1300,
        }),
        display: 'flex',
        alignItems: 'center',
        gap: 1,
        padding: '4px',
        backgroundColor: 'transparent',
        borderRadius: '24px',
        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
        '&:hover': {
          transform: 'scale(1.02)',
        },
        '&:active': {
          transform: 'scale(0.98)',
        }
      }}
    >
      {/* Custom Toggle Switch */}
      <Box
        onClick={toggleTheme}
        sx={{
          position: 'relative',
          width: 65,
          height: 32,
          backgroundColor: isDarkMode ? '#000000' : '#ff9800',
          borderRadius: '16px',
          cursor: 'pointer',
          transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
          display: 'flex',
          alignItems: 'center',
          padding: '3px',
          '&:hover': {
            transform: 'scale(1.05)',
            boxShadow: isDarkMode
              ? '0 4px 12px rgba(0, 0, 0, 0.3)'
              : '0 4px 12px rgba(255, 152, 0, 0.3)',
          }
        }}
      >
        {/* Icon on the left */}
        <Box
          sx={{
            position: 'absolute',
            left: '8px',
            top: '50%',
            transform: 'translateY(-50%)',
            zIndex: 1,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: '24px',
            height: '24px',
          }}
        >
          {isDarkMode ? (
            <NightlightRound sx={{
              color: 'white',
              fontSize: '18px',
              filter: 'drop-shadow(0 1px 2px rgba(0, 0, 0, 0.3))',
            }} />
          ) : (
            <WbSunny sx={{
              color: 'white',
              fontSize: '18px',
              filter: 'drop-shadow(0 1px 2px rgba(0, 0, 0, 0.3))',
            }} />
          )}
        </Box>

        {/* Circular knob */}
        <Box
          sx={{
            width: 26,
            height: 26,
            backgroundColor: 'white',
            borderRadius: '50%',
            position: 'absolute',
            right: '3px',
            top: '50%',
            transform: isDarkMode ? 'translateY(-50%) translateX(0)' : 'translateY(-50%) translateX(-36px)',
            transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
            boxShadow: '0 2px 4px rgba(0, 0, 0, 0.2)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 2,
          }}
        >
          {/* Icon inside the knob */}
          {isDarkMode ? (
            <WbSunny sx={{
              color: '#ff9800',
              fontSize: '16px',
            }} />
          ) : (
            <NightlightRound sx={{
              color: '#000000',
              fontSize: '16px',
            }} />
          )}
        </Box>
      </Box>
    </Box>
  );
};
